import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchProductBySlug, isBestsellerProduct, fetchProductReviews, fetchProductVariations, getSaleBadgeText, isProductOnSale, getProductFormula, decodeText, formatTextWithBreaks } from '../api/woocommerce';
import { useCart } from '../components/CartContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';

function round(value, precision) {
  var multiplier = Math.pow(10, precision || 0);
  return Math.round(value * multiplier) / multiplier;
}


const FilledStarL = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="black" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0L15.705 7.905L24 9.165L18 15.315L19.41 24L12 19.905L4.59 24L6 15.315L0 9.165L8.295 7.905L12 0Z" />
  </svg>
);

const EmptyStarL = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="black" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3.525L14.355 8.535L14.7 9.255L15.495 9.375L20.895 10.2L16.935 14.265L16.41 14.805L16.53 15.555L17.445 21.195L12.735 18.585L12.015 18.18L11.295 18.585L6.585 21.195L7.5 15.555L7.62 14.805L7.095 14.265L3.135 10.2L8.535 9.375L9.33 9.255L9.675 8.535L12.03 3.525M12 0L8.295 7.905L0 9.165L6 15.315L4.59 24L12 19.905L19.41 24L18 15.315L24 9.165L15.705 7.905L12 0Z" />
  </svg>
);


export const StarRatingLarge = ({ rating }) => {
  return (
    <div className="flex items-center">
      {[1, 2, 3, 4, 5].map((index) => (
        <span key={index} className="mr-[8px]">
          {index <= rating ? <FilledStarL /> : <EmptyStarL />}
        </span>
      ))}
    </div>
  );
};

// Star Rating component for display
const StarRating = ({ rating }) => {
  const FilledStar = () => (
    <svg
      width="8"
      height="8"
      viewBox="0 0 8 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4 0L5.235 2.635L8 3.055L6 5.105L6.47 8L4 6.635L1.53 8L2 5.105L0 3.055L2.765 2.635L4 0Z"
        fill="black"
      />
    </svg>
  );

  const OutlinedStar = () => (
    <svg
      width="8"
      height="8"
      viewBox="0 0 8 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_4001_208)">
        <path
          d="M4 1.175L4.785 2.845L4.9 3.085L5.165 3.125L6.965 3.4L5.645 4.755L5.47 4.935L5.51 5.185L5.815 7.065L4.245 6.195L4.005 6.06L3.765 6.195L2.195 7.065L2.5 5.185L2.54 4.935L2.365 4.755L1.045 3.4L2.845 3.125L3.11 3.085L3.225 2.845L4.01 1.175M4 0L2.765 2.635L0 3.055L2 5.105L1.53 8L4 6.635L6.47 8L6 5.105L8 3.055L5.235 2.635L4 0Z"
          fill="black"
        />
      </g>
      <defs>
        <clipPath id="clip0_4001_208">
          <rect width="8" height="8" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
  return (
    <div className="flex items-center translate-y-[1px]">
      {[1, 2, 3, 4, 5].map((index) => (
        <span key={index} className='mr-[4px]'>
          {index <= rating ? <FilledStar /> : <OutlinedStar />}
        </span>
      ))}
    </div>
  );
};

const ProductPage = () => {
  const [id, setId] = useState();
  const { slug } = useParams();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [variations, setVariations] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { currentLanguage } = useLanguage();
  const { formatPrice } = useCurrency();


  const handlePreviousImage = () => {
    if (product.images && product.images.length > 1) {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === 0 ? product.images.length - 1 : prevIndex - 1
      );
    }
  };

  const handleNextImage = () => {
    if (product.images && product.images.length > 1) {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === product.images.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  // Use cart context
  const { addToCart, openCart } = useCart();
  const { t } = useTranslation();

  // {t('cart.remove')} the local fetchVariations function entirely
  // In the useEffect, replace the variation fetching logic:
  useEffect(() => {
    const loadProductAndReviews = async () => {
      try {
        setLoading(true);
        let productData = await fetchProductBySlug(slug, currentLanguage);


        if (productData.id) {
          setId(productData.id);
        }
        // Wait for all data to be ready before setting loading to false
        const promises = [];

        // Set product data
        setProduct(productData);

        // Load variations if they exist
        if (productData.id && productData.variations && productData.variations.length > 0) {
          promises.push(
            fetchProductVariations(productData.id)
              .then(variationData => {
                setVariations(variationData);
                if (variationData.length > 0) {
                  setSelectedVariation(variationData[0]);
                }
              })
              .catch(variationError => {
                console.error('Error loading variations:', variationError);
                const defaultVariation = {
                  id: productData.id,
                  price: formatPrice(productData.price),
                  regular_price: formatPrice(productData.regular_price),
                  sale_price: formatPrice(productData.sale_price),
                  attributes: [],
                  stock_status: productData.stock_status
                };
                setVariations([defaultVariation]);
                setSelectedVariation(defaultVariation);
              })
          );
        } else {
          const defaultVariation = {
            id: productData.id,
            price: formatPrice(productData.price),
            regular_price: formatPrice(productData.regular_price),
            sale_price: formatPrice(productData.sale_price),
            attributes: [],
            stock_status: productData.stock_status
          };
          setVariations([defaultVariation]);
          setSelectedVariation(defaultVariation);
        }

        // Load reviews
        promises.push(
          fetchProductReviews(productData.id)
            .then(reviewsData => setReviews(reviewsData))
            .catch(reviewErr => console.error('Error loading reviews:', reviewErr))
        );

        // Wait for all data to be loaded
        await Promise.all(promises);

        // Add a small delay to ensure smooth transition
        setTimeout(() => {
          setLoading(false);
        }, 100);

      } catch (err) {
        console.error('Error loading product:', err);
        setError('Failed to load product details');
        setLoading(false);
      }
    };

    if (slug) {
      loadProductAndReviews();
    }
  }, [slug, currentLanguage]);

  const badgeRef = useRef(null);
  const [squareSize, setSquareSize] = useState(0);

  useEffect(() => {
    const updateSize = () => {
      if (badgeRef.current) {
        const height = badgeRef.current.offsetHeight;
        setSquareSize(height);
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);

    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;
  // Add these touch handler functions after your existing image navigation functions
  const onTouchStart = (e) => {
    setTouchEnd(null); // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNextImage();
    }
    if (isRightSwipe) {
      handlePreviousImage();
    }
  };


  if (error || !product) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        {/* <Link to="/" className="mt-4 inline-block px-4 py-2 bg-black text-white">
          Back to Shop
        </Link> */}
      </div>
    );
  }
  if (loading) {
    return (
      <div className="px-2">
        <div className="flex flex-col md:flex-row gap-2 w-full">
          {/* Loading skeleton for image */}
          <div className="w-full md:w-1/2 aspect-square bg-[#BDBDBD] animate-pulse border border-black"></div>

          {/* Loading skeleton for product info */}
          <div className="w-full md:w-1/2 flex flex-col">
            <div className="h-8 bg-[#BDBDBD] animate-pulse mb-4"></div>
            <div className="h-4 bg-[#BDBDBD] animate-pulse mb-2 w-3/4"></div>
            <div className="h-4 bg-[#BDBDBD] animate-pulse mb-4 w-1/2"></div>
            <div className="h-20 bg-[#BDBDBD] animate-pulse mb-4"></div>
            <div className="h-10 bg-[#BDBDBD] animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <p>Error loading product</p>
      </div>
    );
  }
  // Helper function to get ACF field with fallback
  const getAcfField = (fieldName, fallback = '') => {
    return product.acf && product.acf[fieldName] ? product.acf[fieldName] : fallback;
  };

  // Get size options from product attributes
  const getSizeOptions = () => {
    const sizeAttribute = product.attributes?.find(attr => attr.name.toLowerCase() === 'size');
    return sizeAttribute ? sizeAttribute.options : [];
  };

  // Handle variation selection
  const handleVariationSelect = (variation) => {
    setSelectedVariation(variation);
  };

  // Get current raw price value (for calculations)
  const getCurrentRawPrice = () => {
    if (!selectedVariation) return product.price;

    // Return sale price if available, otherwise regular price or price
    return selectedVariation.sale_price || selectedVariation.price || selectedVariation.regular_price;
  };

  // Get current formatted price (for display)
  const getCurrentPrice = () => {
    return formatPrice(getCurrentRawPrice());
  };

  // Get variation by size option
  const getVariationBySize = (sizeOption) => {
    return variations.find(variation => {
      const sizeAttr = variation.attributes?.find(attr => attr.name.toLowerCase() === 'size');
      return sizeAttr && sizeAttr.option === sizeOption;
    });
  };

  // Handle add to cart
  const handleAddToCart = () => {
    if (!selectedVariation) return;

    // Create a product object for the cart with proper variation structure
    const productFormula = getProductFormula(product);
    const productNumber = getAcfField("product_number");

    const cartProduct = {
      id: product.id,
      variation_id: selectedVariation.id,
      name: product.name,
      price: getCurrentRawPrice(),
      images: product.images,
      variation: selectedVariation,
      formula: productFormula && productNumber ? `${productFormula.slug}${productNumber}` : ''
    };

    // Add to cart with quantity 1
    addToCart(cartProduct, 1);

    // Open the cart to show the added item
    openCart();
  };

  // Calculate average rating from reviews
  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length)
    : parseFloat(product.average_rating) || 0;

  const sizeOptions = getSizeOptions();

  return (
    <div className='px-2'>
      {/* Product Detail Section */}
      <div>
        <div className="flex flex-col md:flex-row gap-2 w-full">
          {/* Product Image */}
          <div
            className="w-full md:w-1/2 relative aspect-square"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >            {product.images && product.images.length > 0 ? (
            <img
              src={product.images[currentImageIndex].src}
              alt={decodeText(product.name)}
              className="w-full h-full object-cover border border-black aspect-square"
            />
          ) : (
            <div className="bg-[#F5F5F5] w-full h-full flex items-center justify-center border border-black aspect-square">
              <p>{t('productGrid.noImage')}</p>
            </div>
          )}

            {/* Badges container - positioned horizontally */}
            {(isProductOnSale(product) || isBestsellerProduct(product)) && (
              <div className="absolute top-[16px] right-[16px] flex gap-2.5 z-10">
                {/* Sale badge - appears first (leftmost) if product is on sale */}
                {isProductOnSale(product) && (
                  <div className="border border-black flex items-center bg-white">
                    <span className='text-14 mt-[5px] mb-[5px] ml-[7px] mr-2 font-normal'>{getSaleBadgeText(product)}</span>
                  </div>
                )}

                {/* {t('productGrid.bestseller')} badge - appears after sale badge */}
                {isBestsellerProduct(product) && (
                  <div className="border border-black flex items-center bg-white">
                    <svg className="mt-[5px] mb-[5px] ml-[7px]"
                      width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.99994 1.18182L8.41813 3.58182L11.1163 2.88364L10.4181 5.58182L12.8181 7.00001L10.4181 8.41819L11.1163 11.1164L8.41813 10.4182L6.99994 12.8182L5.58176 10.4182L2.88358 11.1164L3.58176 8.41819L1.18176 7.00001L3.58176 5.58182L2.88358 2.88364L5.58176 3.58182L6.99994 1.18182Z" fill="black" stroke="black" strokeMiterlimit="10" />
                    </svg>
                    <span className='text-14 font-normal ml-[3px] mr-2 mb-[4px] mt-[5px]'>{t('productGrid.bestseller')}</span>
                  </div>
                )}
              </div>
            )}

            {/* Navigation arrows - only show if there are multiple images */}
            {product.images && product.images.length > 1 && (
              <div className="absolute left-3 right-3 flex justify-between top-1/2 transform -translate-y-1/2">
                <button
                  onClick={handlePreviousImage}
                  className="transition-colors rounded-full w-8 h-8 md:w-10 md:h-10 flex items-center justify-start pl-1"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_2032_136)">
                      <path d="M11.6364 14.9091L5.0909 8L11.6364 1.09091" stroke="black" stroke-miterlimit="10" />
                    </g>
                    <defs>
                      <clipPath id="clip0_2032_136">
                        <rect width="16" height="16" fill="white" transform="translate(16 16) rotate(-180)" />
                      </clipPath>
                    </defs>
                  </svg>
                </button>
                <button
                  onClick={handleNextImage}
                  className="transition-colors rounded-full w-8 h-8 md:w-10 md:h-10 flex items-center justify-end pr-1"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_2032_133)">
                      <path d="M4.36353 1.09091L10.909 8L4.36353 14.9091" stroke="black" stroke-miterlimit="10" />
                    </g>
                    <defs>
                      <clipPath id="clip0_2032_133">
                        <rect width="16" height="16" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="w-full md:w-1/2 flex flex-col md:aspect-square md:h-full">
            {/* Scrollable info */}
            <div className="flex-1 overflow-y-auto">
              <div className="flex justify-between items-stretch mb-[16px]">
                <div className="flex-1">
                  <h1 className="text-26 font-light mb-[7px]">{decodeText(product.name)}</h1>
                  <div className="flex items-baseline">
                    <StarRating rating={Math.round(averageRating)} />
                    <span className="ml-[2px] text-10 font-normal">
                      {t('product.reviewsCount', { count: reviews.length || product.rating_count || 0 })}
                    </span>
                    <span className="ml-[3px] text-10 font-normal">
                      {t('product.averageRating', { rating: round(averageRating, 1) })}
                    </span>
                  </div>
                </div>
                {(() => {
                  const formula = getProductFormula(product);
                  const productNumber = getAcfField("product_number");
                  return formula && productNumber && (
                    <div
                      className="w-12 h-12 grid place-items-center px-2 flex-shrink-0 border border-black"
                      style={{ backgroundColor: formula.color }}
                    >
                      <span className="whitespace-nowrap text-white text-14 font-normal text-center" style={{ color: formula.text_color }}>
                        {formula.slug}{productNumber}
                      </span>
                    </div>
                  );
                })()}
              </div>

              {/* Dynamic Price Display */}
              <div className="mb-4">
                {selectedVariation && (selectedVariation.sale_price || product.sale_price) ? (
                  <p className="text-18 font-normal">
                    <span className="line-through">
                      {formatPrice(selectedVariation ? selectedVariation.regular_price : product.regular_price)}
                    </span>{' '}
                    {getCurrentPrice()}
                  </p>
                ) : (
                  <p className="text-18 font-normal">{getCurrentPrice()}</p>
                )}
              </div>

              <div>
                <p
                  className="font-light text-14"
                  dangerouslySetInnerHTML={{
                    __html: formatTextWithBreaks(product.description.replace(/<\/?p>/g, ''))
                  }}
                />
                {getAcfField('bold_text_after_description') &&
                  <div>
                    <p
                      className="text-14 font-regular mt-[4px]"
                      dangerouslySetInnerHTML={{
                        __html: formatTextWithBreaks(getAcfField('bold_text_after_description'))
                      }}
                    />
                  </div>
                }
              </div>

              {/* Product Specifications */}
              <div className="mb-[40px] md:mb-8">
                {[
                  [getAcfField('product_spec_title_1'), getAcfField('product_spec_description_1')],
                  [getAcfField('product_spec_title_2'), getAcfField('product_spec_description_2')],
                  [getAcfField('product_spec_title_3'), getAcfField('product_spec_description_3')],
                  [getAcfField('product_spec_title_4'), getAcfField('product_spec_description_4'), getAcfField('product_spec_bold_4')],
                  [getAcfField('product_spec_title_5'), getAcfField('product_spec_description_5')],
                  [getAcfField('product_spec_title_6'), getAcfField('product_spec_description_6')],
                ].map(([label, value, bold], index) => (
                  (label || value) &&
                  <div className="flex flex-col" key={index}>
                    <hr className="border-t border-black mt-2" />
                    <div className="mt-2">
                      <p className=" text-14 font-normal mb-[4px]">{decodeText(label)}</p>
                    </div>
                    <div>
                      <p
                        className="text-14 font-light"
                        dangerouslySetInnerHTML={{
                          __html: formatTextWithBreaks(value)
                        }}
                      />
                    </div>
                    {bold && <div>
                      <p
                        className="text-14 font-regular mt-[4px]"
                        dangerouslySetInnerHTML={{
                          __html: formatTextWithBreaks(bold)
                        }}
                      />
                    </div>}
                  </div>
                ))}
                <hr className="border-t border-black mt-2" />
              </div>
            </div>

            {sizeOptions.length === 1 && (
              <div className="mb-2">
                {sizeOptions.map((sizeOption, index) => {
                  const variation = getVariationBySize(sizeOption);
                  const isSelected = selectedVariation && selectedVariation.id === variation?.id;
                  const isAvailable = variation && variation.stock_status === 'instock';

                  return (
                    <div
                      key={index}
                      className={`border text-center cursor-pointer transition-colors ${!isAvailable
                        ? 'border-[#BDBDBD] cursor-not-allowed opacity-50'
                        : isSelected
                          ? 'border-black'
                          : 'border-[#BDBDBD]'
                        }`}
                      onClick={() => {
                        if (variation && isAvailable) {
                          handleVariationSelect(variation);
                        }
                      }}
                    >
                      <div
                        className={`text-14 pt-[10px] pb-[9px]  ${isSelected ? 'text-black font-normal' : 'text-black font-light'
                          }`}
                      >
                        {sizeOption}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {sizeOptions.length === 2 && (
              <div className="grid grid-cols-2 md:grid-cols-2 gap-2 mb-2">
                {sizeOptions.map((sizeOption, index) => {
                  const variation = getVariationBySize(sizeOption);
                  const isSelected = selectedVariation && selectedVariation.id === variation?.id;
                  const isAvailable = variation && variation.stock_status === 'instock';

                  return (
                    <div
                      key={index}
                      className={`border text-center cursor-pointer transition-colors ${!isAvailable
                        ? 'border-[#BDBDBD] cursor-not-allowed opacity-50'
                        : isSelected
                          ? 'border-black'
                          : 'border-[#BDBDBD]'
                        }`}
                      onClick={() => {
                        if (variation && isAvailable) {
                          handleVariationSelect(variation);
                        }
                      }}
                    >
                      <div
                        className={`text-14 pt-[10px] pb-[9px] text-black ${isSelected ? 'font-normal' : 'font-light'
                          }`}
                      >
                        {sizeOption}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {sizeOptions.length > 2 && (
              <div className="flex flex-col md:grid md:grid-cols-3 gap-2 mb-2">
                {sizeOptions.map((sizeOption, index) => {
                  const variation = getVariationBySize(sizeOption);
                  const isSelected = selectedVariation && selectedVariation.id === variation?.id;
                  const isAvailable = variation && variation.stock_status === 'instock';

                  return (
                    <div
                      key={index}
                      className={`border text-center cursor-pointer transition-colors ${!isAvailable
                        ? 'border-[#BDBDBD] cursor-not-allowed opacity-50'
                        : isSelected
                          ? 'border-black'
                          : 'border-[#BDBDBD]'
                        }`}
                      onClick={() => {
                        if (variation && isAvailable) {
                          handleVariationSelect(variation);
                        }
                      }}
                    >
                      <div
                        className={`text-14 pt-[10px] pb-[9px] text-black ${isSelected ? 'font-normal' : 'font-light'
                          }`}
                      >
                        {sizeOption}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}


            <button
              className={`w-full pt-[10px] pb-[9px] text-14 font-normal transition-colors ${selectedVariation && selectedVariation.stock_status === 'instock'
                ? 'bg-black text-white'
                : 'bg-[#BDBDBD] text-white cursor-not-allowed'
                }`}
              onClick={handleAddToCart}
              disabled={!selectedVariation || selectedVariation.stock_status !== 'instock'}
            >
              {getAcfField("preorder_copy")
                ? t('product.preOrder')
                : !selectedVariation
                  ? t('product.selectSize')
                  : selectedVariation.stock_status === 'instock'
                    ? t('product.addToCart')
                    : t('product.outOfStock')}
            </button>
          </div>
        </div>
      </div>


      {/* Medical Skincare Section - Hidden when multiple formulas selected */}
      {(() => {
        const formula = getProductFormula(product);
        return formula && (
          <div className="py-2 mt-0">
            <div className="w-full flex">
              <div className="flex flex-col md:flex-row gap-2 w-full">
                {/* Mobile: Video first, Desktop: Text first */}
                <div className="order-2 md:order-1 w-full md:w-1/2 flex flex-col justify-start">
                  <h2 className="text-26 font-light mb-2">
                    {currentLanguage === 'mk' && formula.name_mk ? formula.name_mk : (formula.name || 'Medical Skincare [m formulas]')}
                  </h2>
                  <p
                    className="text-14 font-light"
                    dangerouslySetInnerHTML={{
                      __html: (() => {
                        const description = currentLanguage === 'mk' && formula.description_mk 
                          ? formula.description_mk 
                          : (formula.description || formula.description_en || '');
                        return description 
                          ? formatTextWithBreaks(description)
                          : 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce gravida non dolor vestibulum facilisis facilisis leo quis posuere. Suspendisse id ligula elit. Sed facilisis purus orci, et elementum augue maximus id. Aenean a tempor augue aliquet placerat. Mauris tristique facilisis nisi, et tincidunt massa semper convallis arcu. Interdum vel ex. In nec nunc tortor. Donec porta justo eu odio semper gravida sit amet et nisl. Aenean eros lorem, aliquet id mattis ac, elementum at risus. Maecenas hendrerit metus vitae augue elementum scelerisque ut non risus.';
                      })()
                    }}
                  />
                </div>
                {/* Mobile: Video first, Desktop: Video second */}
                <div className="order-1 md:order-2 w-full md:w-1/2 aspect-square">
                  {formula.video ? (
                    <video
                      className="w-full h-full object-cover border border-black aspect-square"
                      src={formula.video}
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      loading="lazy"
                      poster=""
                    />
                  ) : (
                    <div className="bg-[#BDBDBD] w-full h-full flex items-center justify-start border border-black aspect-square">
                      <p>No video available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* {t('reviews.title')} Section */}
      <div className={!getProductFormula(product) ? 'mt-2' : ''}>
        <hr className="border-t border-black" />
        <h2 className="text-26 font-light mb-2 mt-2">{t('reviews.title')}</h2>
        <h3 className="text-26 font-normal mb-2">{product.name.toLowerCase()}</h3>
        <hr className="border-t border-black" />
        <div className="flex flex-col lg:flex-row gap-2 w-full">
          <div className="w-full lg:w-1/2 flex flex-col justify-center md:pb-4 pt-4 pb-[7px]">
            <div className="text-48 font-light mb-[16px] leading-48">
              {averageRating.toFixed(1)}
            </div>
            <div className="flex items-end mb-4 lg:mb-0">
              <StarRatingLarge rating={Math.round(averageRating)} />
              <span className="ml-[16px] text-18 font-light transform translate-y-[3px]">
                {t('product.reviewsCount', { count: reviews.length || 0 })}
              </span>
            </div>
            {/* Mobile line separator */}
            <hr className="border-t border-black lg:hidden" />
          </div>

          <div className="w-full lg:w-1/2 flex flex-col justify-center md:border-l border-black pb-[9px] md:pt-[15px] lg:pl-2">
            <span className='text-48 font-light leading-48'>
              {reviews.length > 0
                ? Math.round((reviews.filter(r => r.recommend === 'yes').length / reviews.length) * 100)
                : 0}% {t('reviews.people')}
            </span>
            <div className='text-48 font-light leading-48'>{t('reviews.wouldRecommend')}</div>
          </div>
        </div>
        <hr className="border-t border-black" />
        {/* Individual Reviews */}
        <div className='pb-2 border-b border-black'>
          {reviews && reviews.length > 0 ? (
            reviews.slice(0, 4).map((review) => {
              // Remove HTML tags (like <p>) from review content
              const cleanReview = review.review.replace(/<[^>]+>/g, '');

              // Extract headline from the review HTML
              const headlineMatch = review.review.match(/<strong>(.*?)<\/strong>/);
              const headline = headlineMatch ? headlineMatch[1] : '';

              // Remove the headline from the main review content
              const reviewWithoutHeadline = review.review
                .replace(/<p><strong>.*?<\/strong><\/p>\s*/g, '')
                .replace(/<[^>]+>/g, '');

              const wouldRecommend = review.recommend === 'yes';

              return (
                <div key={review.id} className="border-b border-black">
                  {headline && (
                    <p className="font-normal text-14 mt-4">{headline}</p>
                  )}
                  <p className="font-light text-14 my-4 leading-relaxed">
                    {reviewWithoutHeadline || cleanReview}
                  </p>
                  <div className="text-14 font-normal space-y-2 lg:space-y-0 mb-[16px] lg:flex lg:items-center">
                    <div className="flex items-center">
                      <span className="mr-2">{review.reviewer}</span>
                      <span className="mr-2">/</span>
                      <span className="mr-4">
                        {new Date(review.date_created).toLocaleDateString('en-GB')}
                      </span>
                    </div>
                    <div className="flex items-baseline">
                      <StarRating rating={review.rating} className="translate-y-[5px]" />
                      <span className="ml-[2px] text-10 font-normal">
                        {t('product.averageRating', { rating: round(review.rating, 1) })}
                      </span>
                      <span className="ml-[6px] text-10 font-medium">
                        {wouldRecommend ? t('reviews.WouldRecommend') : t('reviews.wouldNotRecommendReviews')}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })) :
            <div></div>
          }
        </div>

        {/* Review Actions */}
        <div className="space-y-3 md:space-y-2 mb-16">
          <Link
            to={`/product/${id}/review`}
            className="block w-full pt-[10px] pb-[9px] bg-black text-white text-14 font-normal text-center "
          >
            {t('product.submitReview')}
          </Link>


          <Link
            to={`/product/${id}/reviews`}
            className="block w-full text-center transition-colors text-14 font-normal underline m-0"
          >
            {t('product.seeAllReviews')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;